import { useQuery } from '@tanstack/react-query';
import { flexRender } from '@tanstack/react-table';
import {
  ActiveSourcingChannelDetails,
  SourcingService,
} from 'api-clients/monolith';
import clsx from 'clsx';
import _range from 'lodash/range';
import { useEffect, useMemo } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { useOpeningContext } from '@/contexts/opening-context';
import { useDataTable } from '@/hooks/use-data-table';
import { queryKeys } from '@/query-keys';

import { useChannelColumns } from './columns';
import { useReachChannels } from './use-reach-channels';

interface ReachChannelsProps {
  sourcingPurchaseId: string;
  gridTemplateColumns: string;
}

export function ReachChannels({
  sourcingPurchaseId,
  gridTemplateColumns,
}: ReachChannelsProps) {
  const { reachPublishers, setReachPublishers, setReachPublisherStatus } =
    useReachChannels();
  const { hiringGoalId, displayPrefs } = useOpeningContext();

  const { data, isError, isFetching } = useQuery({
    queryKey: [
      ...queryKeys.openingDetails.channels.reach,
      sourcingPurchaseId,
      displayPrefs.showAggregateData,
      hiringGoalId,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingSourcingPurchasesCampaignDetails(
        sourcingPurchaseId,
        displayPrefs.showAggregateData,
        hiringGoalId,
      ),
  });

  if (isError) {
    throw new Error('Error fetching channels');
  }

  const campaigns = useMemo(() => data?.reach_campaigns ?? [], [data]);

  useEffect(() => {
    setReachPublishers(campaigns);
  }, [campaigns, setReachPublishers]);

  const columns = useChannelColumns({
    setReachPublisherStatus,
  });

  const { table } = useDataTable<ActiveSourcingChannelDetails>({
    columns,
    data: reachPublishers,
    rowCount: reachPublishers.length,
  });

  if (isFetching)
    return (
      <ReachChannelsSkeleton
        gridTemplateColumns={gridTemplateColumns}
        columnsLength={columns.length}
      />
    );

  return (
    <>
      {table.getRowModel().rows.map(row => (
        <div
          key={row.id}
          className="reach-campaigns-text grid items-start"
          style={{ gridTemplateColumns }}
        >
          {row.getVisibleCells().map(cell => (
            <div
              key={cell.id}
              className={clsx(
                'flex flex-col items-start gap-2 py-6 px-5',
                cell.column.id === 'edit' && 'place-self-center',
              )}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

function ReachChannelsSkeleton({
  gridTemplateColumns,
  columnsLength,
}: {
  gridTemplateColumns: string;
  columnsLength: number;
}) {
  return (
    <>
      {_range(2).map(rowIndex => (
        <div
          key={rowIndex}
          className="grid items-center"
          style={{ gridTemplateColumns }}
        >
          <div className="h-21 flex flex-col items-start gap-2 py-6 px-5">
            <div className="flex items-center gap-2">
              <Skeleton className="h-6 w-6" />
              <Skeleton className="h-5 w-24" />
            </div>
          </div>
          {_range(columnsLength - 2).map(colIndex => (
            <div
              key={colIndex}
              className="h-21 flex flex-col items-start gap-3 py-6 px-5"
            >
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-1.5 w-full" />
            </div>
          ))}
          <div className="h-21 flex flex-col items-center justify-between gap-2 py-6 px-5">
            <Skeleton className="h-4 w-8" />
          </div>
        </div>
      ))}
    </>
  );
}
